'use client';

import type React from 'react';
import { CopyButton } from './copy-button';

export interface CopyableFieldProps {
  /** Visible label rendered to the left of the value (e.g. "Meeting ID"). */
  readonly label: string;
  /** The value that is both displayed and copied to the clipboard. */
  readonly value: string;
  /**
   * Override the CopyButton's aria-label / title. Defaults to
   * `"Copy {label.toLowerCase()}"`.
   */
  readonly copyLabel?: string;
  /** Passed through to the underlying CopyButton. Default 1500ms. */
  readonly flashMs?: number;
}

/**
 * Label + value + inline CopyButton, for rendering a one-line identifier
 * (meeting ID, passcode, room code) that users are likely to copy in full.
 *
 * The value text is `select-all` so keyboard users can copy via Cmd/Ctrl+C
 * without reaching for the button.
 */
export function CopyableField({
  label,
  value,
  copyLabel,
  flashMs,
}: CopyableFieldProps): React.JSX.Element {
  return (
    <div className="text-xs flex items-center gap-1">
      <span className="text-muted-foreground">{label}:</span>
      <span className="text-foreground select-all">{value}</span>
      <CopyButton
        value={value}
        label={copyLabel ?? `Copy ${label.toLowerCase()}`}
        {...(flashMs != null && { flashMs })}
      />
    </div>
  );
}
